import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import qs from 'qs';
import IconChevronLeft from '~~elements/static/IconChevronLeft.svg';
import Button from '~~elements/Button';
import { useQuery } from '~~utils/CommonUtils';
import toast from '~~elements/Toast';
import {
  validTeamData,
  updateTeamData,
  validStaffsData,
  updateStaffsData,
  validPlayersData,
  updatePlayersData,
} from './service';
import UTRMultilingual from '../Multilingual';
import { StyledDiv } from '../../Settings/Styles';
import OverviewSection from './components/OverviewSection';
import TeamSection from './components/TeamSection';
import StaffsSection from './components/StaffsSection';
import { useRegistryDispatch } from '../useUserTournamentRegistry';
import PlayersSection from './components/PlayersSection';
import PDFSection from './components/PDFSection';
import ReviewSection from './components/ReviewSection';

const UserTournamentRegistry = ({ teamInRegistry, registry }) => {
  const query = useQuery();
  const part = query.get('part') ? query.get('part') : 'overview';

  const dispatch = useRegistryDispatch();

  const [editData, setEditData] = useState(null);
  const [editApiInProgress, setEditApiInProgress] = useState(false);

  const cancelEditing = () => {
    setEditData(null);
    query.set({ part: 'overview' });
  };

  const handleEditSubmit = () => {
    if (!editData) {
      return;
    }

    let targetMethod = null;
    let targetData = null;
    let afterSuccess = (newTeamInRegistry) => {
      dispatch({
        type: 'UPDATE_TEAM_IN_REGISTRY',
        payload: newTeamInRegistry,
      });
    };
    switch (part) {
      case 'team': {
        const { error, message, data: validData } = validTeamData(editData);
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetMethod = updateTeamData;
        targetData = validData;
        break;
      }
      case 'staff': {
        const { error, message, data: validData } = validStaffsData(editData);
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetMethod = updateStaffsData;
        targetData = validData;
        break;
      }
      case 'player': {
        const { error, message, data: validData } = validPlayersData(editData);
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetMethod = updatePlayersData;
        targetData = validData;
        break;
      }
      default:
        return;
    }

    if (!targetMethod) {
      return;
    }

    setEditApiInProgress(true);
    targetMethod(teamInRegistry.registry_uniqid, teamInRegistry.uniqid, targetData)
      .then((responseData) => {
        toast(UTRMultilingual(`SUCCESS.UPDATE`), { status: 'success' });
        afterSuccess(responseData);
      })
      .catch(() => {
        toast(UTRMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setEditApiInProgress(false);
        cancelEditing();
      });
  };

  const partLink = (targetPart) => {
    if (editData && part !== targetPart) {
      return (
        <label className={`section-list--item disabled ${targetPart}`}>
          {UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.${targetPart.toUpperCase()}`)}
        </label>
      );
    }

    return (
      <Link
        className={`section-list--item ${targetPart} ${part === targetPart && 'selected'}`}
        to={(location) => {
          const currentSearchValue = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
          currentSearchValue.part = targetPart;
          return {
            ...location,
            search: qs.stringify(currentSearchValue),
          };
        }}
      >
        {UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.${targetPart.toUpperCase()}`)}
      </Link>
    );
  };

  const getSection = () => {
    switch (part) {
      case 'overview':
        return <OverviewSection teamInRegistry={teamInRegistry} registry={registry} />;
      case 'team':
        return (
          <TeamSection
            teamInRegistry={teamInRegistry}
            registry={registry}
            onChange={setEditData}
            apiInProgress={editApiInProgress || 'SIGNED_UP' !== teamInRegistry.status}
          />
        );
      case 'staff':
        return (
          <StaffsSection
            teamInRegistry={teamInRegistry}
            registry={registry}
            onChange={setEditData}
            apiInProgress={editApiInProgress || 'SIGNED_UP' !== teamInRegistry.status}
          />
        );
      case 'player':
        return (
          <PlayersSection
            teamInRegistry={teamInRegistry}
            registry={registry}
            onChange={setEditData}
            apiInProgress={editApiInProgress || 'SIGNED_UP' !== teamInRegistry.status}
          />
        );
      case 'pdf':
        return (
          <PDFSection
            teamInRegistry={teamInRegistry}
            registry={registry}
            disabled={'SIGNED_UP' !== teamInRegistry.status}
          />
        );
      case 'review_status':
        return <ReviewSection teamInRegistry={teamInRegistry} registry={registry} />;
      default:
        return <></>;
    }
  };

  return (
    <StyledDiv>
      <div className="greet">
        <div className="greet--name">
          {UTRMultilingual(`CUSTOM.REGISTRY_PANEL`)}
          {` / `}
          {`${teamInRegistry.season.title} - ${teamInRegistry.name}`}
        </div>
      </div>
      <div className="panel">
        <div className="multi-section-list">
          <div className="section-list">
            <div className="section-list--title" />
            {editData ? (
              <label
                className={`section-list--item ${editApiInProgress && `disabled`}`}
                onClick={editApiInProgress ? () => {} : cancelEditing}
              >
                {UTRMultilingual(`COMMON.CANCEL`)}
              </label>
            ) : (
              <Link
                className="section-list--item"
                to={(location) => {
                  const currentSearchValue = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
                  delete currentSearchValue.team;
                  return {
                    ...location,
                    search: qs.stringify(currentSearchValue),
                  };
                }}
              >
                <img style={{ width: '1rem', height: '1rem' }} src={IconChevronLeft} alt="back" />
                {UTRMultilingual(`COMMON.BACK`)}
              </Link>
            )}
          </div>
          <div className="section-list">
            <div className="section-list--title" />
            {partLink('overview')}
            <div className="section-list--title">{UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.STEP_ONE_SIGNED_UP`)}</div>
            {partLink('team')}
            {partLink('staff')}
            {partLink('player')}
            {partLink('pdf')}
            <div className="section-list--title">{UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.STEP_TWO_REVIEW`)}</div>
            {partLink('review_status')}
            {/* <div className="section-list--title">{UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.STEP_THREE_PAYMENT`)}</div>
            {partLink('payment_status')} */}
          </div>
        </div>
        <div className="section lg">
          <div className="section--title">{UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.${part.toUpperCase()}`)}</div>
          {editData && (
            <div className="section--subtitle">
              {UTRMultilingual(`FORM.UNSAVED_CONTENT_EXIST`)}
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {UTRMultilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
          {getSection()}
          {editData && (
            <div className="section--footer">
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {UTRMultilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </StyledDiv>
  );
};

export default UserTournamentRegistry;
